/**
 * The sections of a job's own page (PF-2, fieldiq-api#1200): crew, owned
 * equipment, rent-ins and the daily-log list. Each is a full-width panel; an
 * add form opens inline in the panel (a row is a child of the job, not a
 * record of its own), and a removal asks in-app first.
 *
 * Writes are PF_DISPATCH. A closed job (completed / cancelled) takes no writes
 * — the API's 409 job_closed — so its forms are not offered. A refusal that
 * still arrives reads as one sentence from `pfRefusal`, never as a green toast.
 */
import React, { useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import {
  useAddPfCrew,
  useAddPfJobEquipment,
  useAddPfRentIn,
  usePfDailyLogs,
  useRemovePfCrew,
  useRemovePfJobEquipment,
  useRemovePfRentIn,
  useUpdatePfRentIn,
} from '../../hooks/usePipelineFacility';
import { blankToNull, fmtNumber, pfDay, pfRefusal, pfRentStatusSkin, withUnit } from '../../lib/pipeline-facility';
import type { PfCatalog, PfCrew, PfDailyLogSummary, PfJobDetail, PfJobEquipment, PfMeta, PfRentIn } from '../../types/pipeline-facility';
import { chainageRange } from './JobsScreen';
import {
  PF_DATE_INPUT,
  PF_INPUT,
  PF_PRIMARY,
  PF_SECONDARY,
  PfBadge,
  PfDialog,
  PfEmpty,
  PfField,
  PfFormError,
  PfPanel,
  PfRegister,
  type PfColumn,
} from './PfBits';

const SETTLED = { isLoading: false };

/** One in-app confirm for a removal; the refusal (if any) shows inside it. */
function RemoveConfirm({
  open,
  title,
  body,
  confirmLabel,
  pending,
  error,
  onClose,
  onConfirm,
}: {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <PfDialog
      open={open}
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={PF_SECONDARY} onClick={onClose} disabled={pending}>
            Keep it
          </button>
          <button type="button" className={PF_PRIMARY} onClick={onConfirm} disabled={pending}>
            {pending ? 'Removing…' : confirmLabel}
          </button>
        </>
      }
    >
      <PfFormError message={error} />
      <p className="text-sm text-fieldiq-text">{body}</p>
    </PfDialog>
  );
}

function RemoveButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className={PF_SECONDARY} aria-label={label} onClick={onClick}>
      <Trash2 className="size-4" aria-hidden="true" />
      Remove
    </button>
  );
}

function AddButton({ label, onClick, disabled }: { label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" className={PF_PRIMARY} onClick={onClick} disabled={disabled}>
      <Plus className="size-4" aria-hidden="true" />
      {label}
    </button>
  );
}

function FormButtons({ pending, onCancel, label }: { pending: boolean; onCancel: () => void; label: string }) {
  return (
    <div className="flex flex-wrap justify-end gap-2">
      <button type="button" className={PF_SECONDARY} onClick={onCancel} disabled={pending}>
        Cancel
      </button>
      <button type="submit" className={PF_PRIMARY} disabled={pending}>
        {pending ? 'Saving…' : label}
      </button>
    </div>
  );
}

// ─── Crew ────────────────────────────────────────────────────────────────────

export function CrewSection({
  job,
  catalog,
  users,
  canWrite,
}: {
  job: PfJobDetail;
  catalog: PfCatalog | null;
  users: { id: string; name: string }[];
  canWrite: boolean;
}) {
  const add = useAddPfCrew();
  const remove = useRemovePfCrew();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ user_id: '', crew_role: '', start_on: '', end_on: '', notes: '' });
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<PfCrew | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const onCrew = useMemo(() => new Set(job.crew.map((c) => c.userId)), [job.crew]);
  const pickable = users.filter((u) => !onCrew.has(u.id));

  const columns: PfColumn<PfCrew>[] = [
    { key: 'name', header: 'Name', render: (c) => <span className="font-extrabold text-white">{c.userName || '—'}</span> },
    { key: 'role', header: 'Crew role', render: (c) => c.crewRoleLabel || c.crewRole || '—' },
    { key: 'start', header: 'From', render: (c) => pfDay(c.startOn) },
    { key: 'end', header: 'To', render: (c) => pfDay(c.endOn) },
    { key: 'notes', header: 'Notes', render: (c) => c.notes || '—' },
    ...(canWrite
      ? [{ key: 'remove', header: '', align: 'right' as const, render: (c: PfCrew) => <RemoveButton label={`Remove ${c.userName ?? 'crew member'}`} onClick={() => { setRemoveError(null); setRemoving(c); }} /> }]
      : []),
  ];

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.user_id) return setError('Choose who to add.');
    if (!form.crew_role) return setError('Choose their crew role.');
    add.mutate(
      {
        jobId: job.id,
        body: {
          user_id: form.user_id,
          crew_role: form.crew_role,
          start_on: blankToNull(form.start_on),
          end_on: blankToNull(form.end_on),
          notes: blankToNull(form.notes),
        },
      },
      {
        onSuccess: () => {
          toast.success('Added to the crew');
          setAdding(false);
          setForm({ user_id: '', crew_role: '', start_on: '', end_on: '', notes: '' });
        },
        onError: (err) => setError(pfRefusal(err, 'They could not be added to the crew.')),
      },
    );
  };

  return (
    <PfPanel
      eyebrow="Crew"
      title="Who works this job"
      testId="pf-job-crew"
      actions={canWrite && !adding ? <AddButton label="Add crew" onClick={() => { setError(null); setAdding(true); }} /> : undefined}
    >
      {adding && canWrite && (
        <form onSubmit={submit} className="space-y-3 border-b border-fieldiq-border px-4 py-4" data-testid="pf-crew-form">
          <PfFormError message={error} />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <PfField label="Person">
              <select className={PF_INPUT} aria-label="Person" value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })}>
                <option value="">Choose…</option>
                {pickable.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </PfField>
            <PfField label="Crew role">
              <select className={PF_INPUT} aria-label="Crew role" value={form.crew_role} onChange={(e) => setForm({ ...form, crew_role: e.target.value })}>
                <option value="">Choose…</option>
                {(catalog?.crewRoles ?? []).map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </PfField>
            <PfField label="From">
              <input type="date" className={PF_DATE_INPUT} aria-label="Crew from" value={form.start_on} onChange={(e) => setForm({ ...form, start_on: e.target.value })} />
            </PfField>
            <PfField label="To">
              <input type="date" className={PF_DATE_INPUT} aria-label="Crew to" value={form.end_on} onChange={(e) => setForm({ ...form, end_on: e.target.value })} />
            </PfField>
            <PfField label="Notes">
              <input className={PF_INPUT} aria-label="Crew notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </PfField>
          </div>
          <FormButtons pending={add.isPending} onCancel={() => setAdding(false)} label="Add to crew" />
        </form>
      )}
      <PfRegister
        query={SETTLED}
        rows={job.crew}
        columns={columns}
        rail={() => 'border-l-fi-steel'}
        subject="crew"
        testId="pf-crew"
        minWidth="min-w-[760px]"
        empty={<PfEmpty>No one is on this job's crew yet.</PfEmpty>}
        card={(c) => (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-extrabold text-white">{c.userName || '—'}</p>
              <p className="mt-0.5 text-xs text-fieldiq-muted">
                {c.crewRoleLabel || c.crewRole || '—'} · {pfDay(c.startOn)} → {pfDay(c.endOn)}
              </p>
            </div>
            {canWrite && <RemoveButton label={`Remove ${c.userName ?? 'crew member'}`} onClick={() => { setRemoveError(null); setRemoving(c); }} />}
          </div>
        )}
      />
      <RemoveConfirm
        open={!!removing}
        title="Remove from crew"
        body={`Takes ${removing?.userName ?? 'this person'} off the crew. Hours already on a daily log stay on it.`}
        confirmLabel="Remove from crew"
        pending={remove.isPending}
        error={removeError}
        onClose={() => setRemoving(null)}
        onConfirm={() =>
          removing &&
          remove.mutate(removing.id, {
            onSuccess: () => {
              toast.success('Removed from the crew');
              setRemoving(null);
            },
            onError: (err) => setRemoveError(pfRefusal(err, 'They could not be removed.')),
          })
        }
      />
    </PfPanel>
  );
}

// ─── Owned equipment ─────────────────────────────────────────────────────────

export function EquipmentSection({
  job,
  equipment,
  canWrite,
}: {
  job: PfJobDetail;
  equipment: { id: string; name: string; unitNumber: string | null; serialNumber: string | null }[];
  canWrite: boolean;
}) {
  const add = useAddPfJobEquipment();
  const remove = useRemovePfJobEquipment();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ equipment_id: '', start_on: '', end_on: '', notes: '' });
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<PfJobEquipment | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const onJob = useMemo(() => new Set(job.equipment.map((e) => e.equipmentId)), [job.equipment]);
  const pickable = equipment.filter((e) => !onJob.has(e.id));
  const name = (e: PfJobEquipment) => e.equipmentName || e.unitNumber || 'this unit';

  const columns: PfColumn<PfJobEquipment>[] = [
    { key: 'unit', header: 'Unit', render: (e) => <span className="font-extrabold text-white">{e.equipmentName || '—'}</span> },
    { key: 'number', header: 'Unit #', render: (e) => <span className="font-mono">{e.unitNumber || '—'}</span> },
    { key: 'serial', header: 'Serial', render: (e) => <span className="font-mono">{e.serialNumber || '—'}</span> },
    { key: 'type', header: 'Type', render: (e) => e.equipmentType || '—' },
    { key: 'start', header: 'From', render: (e) => pfDay(e.startOn) },
    { key: 'end', header: 'To', render: (e) => pfDay(e.endOn) },
    ...(canWrite
      ? [{ key: 'remove', header: '', align: 'right' as const, render: (e: PfJobEquipment) => <RemoveButton label={`Remove ${name(e)}`} onClick={() => { setRemoveError(null); setRemoving(e); }} /> }]
      : []),
  ];

  const submit = (ev: React.FormEvent) => {
    ev.preventDefault();
    setError(null);
    if (!form.equipment_id) return setError('Choose the unit.');
    add.mutate(
      {
        jobId: job.id,
        body: { equipment_id: form.equipment_id, start_on: blankToNull(form.start_on), end_on: blankToNull(form.end_on), notes: blankToNull(form.notes) },
      },
      {
        onSuccess: () => {
          toast.success('Unit added to the job');
          setAdding(false);
          setForm({ equipment_id: '', start_on: '', end_on: '', notes: '' });
        },
        onError: (err) => setError(pfRefusal(err, 'The unit could not be added.')),
      },
    );
  };

  return (
    <PfPanel
      eyebrow="Equipment"
      title="Owned units on this job"
      testId="pf-job-equipment"
      actions={canWrite && !adding ? <AddButton label="Add unit" onClick={() => { setError(null); setAdding(true); }} /> : undefined}
    >
      {adding && canWrite && (
        <form onSubmit={submit} className="space-y-3 border-b border-fieldiq-border px-4 py-4" data-testid="pf-equipment-form">
          <PfFormError message={error} />
          {pickable.length === 0 ? (
            <PfEmpty>Every unit in your equipment list is already on this job, or the list is empty. Add units under Equipment.</PfEmpty>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <PfField label="Unit">
                <select className={PF_INPUT} aria-label="Unit" value={form.equipment_id} onChange={(e) => setForm({ ...form, equipment_id: e.target.value })}>
                  <option value="">Choose…</option>
                  {pickable.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name}
                      {e.unitNumber ? ` · ${e.unitNumber}` : ''}
                    </option>
                  ))}
                </select>
              </PfField>
              <PfField label="From">
                <input type="date" className={PF_DATE_INPUT} aria-label="Unit from" value={form.start_on} onChange={(e) => setForm({ ...form, start_on: e.target.value })} />
              </PfField>
              <PfField label="To">
                <input type="date" className={PF_DATE_INPUT} aria-label="Unit to" value={form.end_on} onChange={(e) => setForm({ ...form, end_on: e.target.value })} />
              </PfField>
              <PfField label="Notes">
                <input className={PF_INPUT} aria-label="Unit notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </PfField>
            </div>
          )}
          <FormButtons pending={add.isPending} onCancel={() => setAdding(false)} label="Add unit" />
        </form>
      )}
      <PfRegister
        query={SETTLED}
        rows={job.equipment}
        columns={columns}
        rail={() => 'border-l-fi-steel'}
        subject="equipment"
        testId="pf-job-unit"
        minWidth="min-w-[820px]"
        empty={<PfEmpty>No owned unit is on this job yet.</PfEmpty>}
        card={(e) => (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-extrabold text-white">{e.equipmentName || '—'}</p>
              <p className="mt-0.5 text-xs text-fieldiq-muted">
                {e.unitNumber || '—'} · {e.equipmentType || '—'}
              </p>
            </div>
            {canWrite && <RemoveButton label={`Remove ${name(e)}`} onClick={() => { setRemoveError(null); setRemoving(e); }} />}
          </div>
        )}
      />
      <RemoveConfirm
        open={!!removing}
        title="Remove unit"
        body={`Takes ${removing ? name(removing) : 'this unit'} off the job.`}
        confirmLabel="Remove unit"
        pending={remove.isPending}
        error={removeError}
        onClose={() => setRemoving(null)}
        onConfirm={() =>
          removing &&
          remove.mutate(removing.id, {
            onSuccess: () => {
              toast.success('Unit removed from the job');
              setRemoving(null);
            },
            onError: (err) => setRemoveError(pfRefusal(err, 'The unit could not be removed.')),
          })
        }
      />
    </PfPanel>
  );
}

// ─── Rent-ins ────────────────────────────────────────────────────────────────

const EMPTY_RENT_IN = {
  vendor_name: '',
  vendor_contact: '',
  description: '',
  unit_number: '',
  equipment_id: '',
  on_rent_on: '',
  off_rent_on: '',
  cost_rate: '',
  rate_unit: 'day',
  billable: true,
  po_number: '',
  notes: '',
};

export function RentInsSection({
  job,
  catalog,
  meta,
  equipment,
  canWrite,
  canSeeCost,
}: {
  job: PfJobDetail;
  catalog: PfCatalog | null;
  meta: PfMeta | null;
  equipment: { id: string; name: string; unitNumber: string | null }[];
  canWrite: boolean;
  canSeeCost: boolean;
}) {
  const add = useAddPfRentIn();
  const update = useUpdatePfRentIn();
  const remove = useRemovePfRentIn();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_RENT_IN);
  const [error, setError] = useState<string | null>(null);
  const [offRent, setOffRent] = useState<{ row: PfRentIn; day: string } | null>(null);
  const [offRentError, setOffRentError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<PfRentIn | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const currency = meta?.currency ?? null;
  const label = (r: PfRentIn) => `${r.vendorName} — ${r.description}`;

  const columns: PfColumn<PfRentIn>[] = [
    {
      key: 'vendor',
      header: 'Vendor',
      render: (r) => (
        <>
          <p className="font-extrabold text-white">{r.vendorName || '—'}</p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">{r.vendorContact || '—'}</p>
        </>
      ),
    },
    {
      key: 'what',
      header: 'Unit',
      render: (r) => (
        <>
          <p>{r.description || '—'}</p>
          <p className="mt-0.5 font-mono text-xs text-fieldiq-muted">{r.unitNumber || r.equipmentName || '—'}</p>
        </>
      ),
    },
    { key: 'on', header: 'On rent', render: (r) => pfDay(r.onRentOn) },
    { key: 'off', header: 'Off rent', render: (r) => pfDay(r.offRentOn) },
    { key: 'days', header: 'On-rent days', align: 'right', render: (r) => displayValue(r.onRentDays ?? undefined) },
    { key: 'hours', header: 'Logged hours', align: 'right', render: (r) => fmtNumber(r.loggedHours) },
    ...(canSeeCost
      ? [
          {
            key: 'rate',
            header: `Rate${currency ? ` (${currency})` : ''}`,
            align: 'right' as const,
            render: (r: PfRentIn) => (r.costRate == null ? '—' : `${fmtNumber(r.costRate)} ${r.rateUnitLabel?.toLowerCase() ?? ''}`.trim()),
          },
          { key: 'billable', header: 'Billable', render: (r: PfRentIn) => (r.billable === undefined ? '—' : r.billable ? 'Yes' : 'No') },
          { key: 'po', header: 'PO', render: (r: PfRentIn) => r.poNumber || '—' },
        ]
      : []),
    { key: 'status', header: 'Status', render: (r) => <PfBadge skin={pfRentStatusSkin(r.rentStatus)} value={r.rentStatus} /> },
    ...(canWrite
      ? [
          {
            key: 'actions',
            header: '',
            align: 'right' as const,
            render: (r: PfRentIn) => (
              <div className="flex justify-end gap-2">
                <button type="button" className={PF_SECONDARY} onClick={() => { setOffRentError(null); setOffRent({ row: r, day: r.offRentOn ?? '' }); }}>
                  Off-rent date
                </button>
                <RemoveButton label={`Remove ${label(r)}`} onClick={() => { setRemoveError(null); setRemoving(r); }} />
              </div>
            ),
          },
        ]
      : []),
  ];

  const set = (k: keyof typeof EMPTY_RENT_IN, v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.vendor_name.trim()) return setError('Enter the vendor.');
    if (!form.description.trim()) return setError('Describe the unit.');
    if (!form.on_rent_on) return setError('Enter the on-rent date.');
    if (form.off_rent_on && form.off_rent_on < form.on_rent_on) return setError('The off-rent date cannot be before the on-rent date.');
    add.mutate(
      {
        jobId: job.id,
        body: {
          vendor_name: form.vendor_name.trim(),
          vendor_contact: blankToNull(form.vendor_contact),
          description: form.description.trim(),
          unit_number: blankToNull(form.unit_number),
          equipment_id: blankToNull(form.equipment_id),
          on_rent_on: form.on_rent_on,
          off_rent_on: blankToNull(form.off_rent_on),
          cost_rate: blankToNull(form.cost_rate),
          rate_unit: form.rate_unit,
          billable: form.billable,
          po_number: blankToNull(form.po_number),
          notes: blankToNull(form.notes),
        },
      },
      {
        onSuccess: () => {
          toast.success('Rent-in added');
          setAdding(false);
          setForm(EMPTY_RENT_IN);
        },
        onError: (err) => setError(pfRefusal(err, 'The rent-in could not be added.')),
      },
    );
  };

  return (
    <PfPanel
      eyebrow="Rent-ins"
      title="Rented-in units"
      testId="pf-job-rent-ins"
      actions={canWrite && !adding ? <AddButton label="Add rent-in" onClick={() => { setError(null); setAdding(true); }} /> : undefined}
    >
      {adding && canWrite && (
        <form onSubmit={submit} className="space-y-3 border-b border-fieldiq-border px-4 py-4" data-testid="pf-rent-in-form">
          <PfFormError message={error} />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <PfField label="Vendor">
              <input className={PF_INPUT} aria-label="Vendor" value={form.vendor_name} onChange={(e) => set('vendor_name', e.target.value)} />
            </PfField>
            <PfField label="Vendor contact">
              <input className={PF_INPUT} aria-label="Vendor contact" value={form.vendor_contact} onChange={(e) => set('vendor_contact', e.target.value)} />
            </PfField>
            <PfField label="Description">
              <input className={PF_INPUT} aria-label="Description" value={form.description} onChange={(e) => set('description', e.target.value)} />
            </PfField>
            <PfField label="Vendor unit #">
              <input className={PF_INPUT} aria-label="Vendor unit number" value={form.unit_number} onChange={(e) => set('unit_number', e.target.value)} />
            </PfField>
            <PfField label="Registered as" hint="Optional — lets a pre-use check be filed against it">
              <select className={PF_INPUT} aria-label="Registered as" value={form.equipment_id} onChange={(e) => set('equipment_id', e.target.value)}>
                <option value="">Not registered</option>
                {equipment.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                    {u.unitNumber ? ` · ${u.unitNumber}` : ''}
                  </option>
                ))}
              </select>
            </PfField>
            <PfField label="On rent">
              <input type="date" className={PF_DATE_INPUT} aria-label="On rent" value={form.on_rent_on} onChange={(e) => set('on_rent_on', e.target.value)} />
            </PfField>
            <PfField label="Off rent">
              <input type="date" className={PF_DATE_INPUT} aria-label="Off rent" value={form.off_rent_on} onChange={(e) => set('off_rent_on', e.target.value)} />
            </PfField>
            <PfField label={`Rate${currency ? ` (${currency})` : ''}`}>
              <input className={PF_INPUT} inputMode="decimal" aria-label="Rate" value={form.cost_rate} onChange={(e) => set('cost_rate', e.target.value)} />
            </PfField>
            <PfField label="Rate per">
              <select className={PF_INPUT} aria-label="Rate per" value={form.rate_unit} onChange={(e) => set('rate_unit', e.target.value)}>
                {(catalog?.rateUnits ?? [{ value: 'day', label: 'Per day' }]).map((u) => (
                  <option key={u.value} value={u.value}>
                    {u.label}
                  </option>
                ))}
              </select>
            </PfField>
            <PfField label="PO number">
              <input className={PF_INPUT} aria-label="Rent-in PO number" value={form.po_number} onChange={(e) => set('po_number', e.target.value)} />
            </PfField>
            <PfField label="Notes">
              <input className={PF_INPUT} aria-label="Rent-in notes" value={form.notes} onChange={(e) => set('notes', e.target.value)} />
            </PfField>
            <label className="flex items-center gap-2 self-end pb-2 text-sm text-fieldiq-text">
              <input type="checkbox" checked={form.billable} onChange={(e) => set('billable', e.target.checked)} />
              Billable to the customer
            </label>
          </div>
          <FormButtons pending={add.isPending} onCancel={() => setAdding(false)} label="Add rent-in" />
        </form>
      )}
      <PfRegister
        query={SETTLED}
        rows={job.rentIns}
        columns={columns}
        rail={(r) => pfRentStatusSkin(r.rentStatus).rail}
        subject="rent-ins"
        testId="pf-rent-in"
        minWidth="min-w-[1100px]"
        empty={<PfEmpty>No unit is rented in for this job.</PfEmpty>}
        card={(r) => (
          <>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-extrabold text-white">{r.vendorName || '—'}</p>
                <p className="mt-0.5 text-xs text-fieldiq-muted">{r.description || '—'}</p>
              </div>
              <PfBadge skin={pfRentStatusSkin(r.rentStatus)} value={r.rentStatus} />
            </div>
            <p className="mt-2 text-xs text-fieldiq-text">
              {pfDay(r.onRentOn)} → {pfDay(r.offRentOn)} · {displayValue(r.onRentDays ?? undefined)} days on rent · {fmtNumber(r.loggedHours)} h logged
            </p>
            {canWrite && (
              <div className="mt-2 flex flex-wrap gap-2">
                <button type="button" className={PF_SECONDARY} onClick={() => { setOffRentError(null); setOffRent({ row: r, day: r.offRentOn ?? '' }); }}>
                  Off-rent date
                </button>
                <RemoveButton label={`Remove ${label(r)}`} onClick={() => { setRemoveError(null); setRemoving(r); }} />
              </div>
            )}
          </>
        )}
      />
      <PfDialog
        open={!!offRent}
        title="Off-rent date"
        onClose={() => setOffRent(null)}
        footer={
          <>
            <button type="button" className={PF_SECONDARY} onClick={() => setOffRent(null)} disabled={update.isPending}>
              Cancel
            </button>
            <button
              type="button"
              className={PF_PRIMARY}
              disabled={update.isPending}
              onClick={() =>
                offRent &&
                update.mutate(
                  { id: offRent.row.id, body: { off_rent_on: blankToNull(offRent.day) } },
                  {
                    onSuccess: () => {
                      toast.success('Off-rent date saved');
                      setOffRent(null);
                    },
                    onError: (err) => setOffRentError(pfRefusal(err, 'The off-rent date could not be saved.')),
                  },
                )
              }
            >
              {update.isPending ? 'Saving…' : 'Save'}
            </button>
          </>
        }
      >
        <PfFormError message={offRentError} />
        <PfField label="Off rent" hint="Leave blank while it is still on rent">
          <input
            type="date"
            className={PF_DATE_INPUT}
            aria-label="Off-rent date"
            value={offRent?.day ?? ''}
            onChange={(e) => offRent && setOffRent({ ...offRent, day: e.target.value })}
          />
        </PfField>
      </PfDialog>
      <RemoveConfirm
        open={!!removing}
        title="Remove rent-in"
        body={`Removes ${removing ? label(removing) : 'this rent-in'} from the job. A unit with logged hours cannot be removed — set its off-rent date instead.`}
        confirmLabel="Remove rent-in"
        pending={remove.isPending}
        error={removeError}
        onClose={() => setRemoving(null)}
        onConfirm={() =>
          removing &&
          remove.mutate(removing.id, {
            onSuccess: () => {
              toast.success('Rent-in removed');
              setRemoving(null);
            },
            onError: (err) => setRemoveError(pfRefusal(err, 'The rent-in could not be removed.')),
          })
        }
      />
    </PfPanel>
  );
}

// ─── Daily logs ──────────────────────────────────────────────────────────────

export function LogsSection({
  job,
  canLog,
  onNew,
  onOpen,
}: {
  job: PfJobDetail;
  canLog: boolean;
  onNew: () => void;
  onOpen: (log: PfDailyLogSummary) => void;
}) {
  const logs = usePfDailyLogs(job.id);
  const rows = logs.data?.data ?? [];
  const chainage = logs.data?.meta.units?.chainage ?? null;
  const columns: PfColumn<PfDailyLogSummary>[] = [
    { key: 'day', header: 'Work date', render: (l) => <span className="font-extrabold text-white">{pfDay(l.workDate)}</span> },
    { key: 'weather', header: 'Weather', render: (l) => l.weather || '—' },
    { key: 'crew', header: 'Crew', align: 'right', render: (l) => displayValue(l.crewCount ?? undefined) },
    { key: 'crewHours', header: 'Crew h', align: 'right', render: (l) => fmtNumber(l.crewHours) },
    { key: 'units', header: 'Units', align: 'right', render: (l) => displayValue(l.unitCount ?? undefined) },
    { key: 'owned', header: 'Owned h', align: 'right', render: (l) => fmtNumber(l.ownedEquipmentHours) },
    { key: 'rent', header: 'Rent-in h', align: 'right', render: (l) => fmtNumber(l.rentInHours) },
    {
      key: 'progress',
      header: `Progress${chainage ? ` (${chainage})` : ''}`,
      render: (l) => <span className="font-mono">{chainageRange(l.progressFromChainage, l.progressToChainage, null)}</span>,
    },
    { key: 'by', header: 'Entered by', render: (l) => l.createdByName || '—' },
  ];
  return (
    <PfPanel
      eyebrow="Daily logs"
      title="Work by day"
      testId="pf-job-logs"
      actions={canLog ? <AddButton label="New daily log" onClick={onNew} /> : undefined}
    >
      <PfRegister
        query={logs}
        rows={rows}
        columns={columns}
        rail={() => 'border-l-fi-amber'}
        onOpen={onOpen}
        subject="daily logs"
        testId="pf-log"
        minWidth="min-w-[900px]"
        empty={<PfEmpty>No daily log has been written for this job.</PfEmpty>}
        card={(l) => (
          <>
            <p className="text-sm font-extrabold text-white">{pfDay(l.workDate)}</p>
            <p className="mt-1 text-xs text-fieldiq-text">
              Crew {displayValue(l.crewCount ?? undefined)} · {withUnit(l.crewHours, 'h')} · Units {displayValue(l.unitCount ?? undefined)} · Progress{' '}
              {chainageRange(l.progressFromChainage, l.progressToChainage, chainage)}
            </p>
          </>
        )}
      />
    </PfPanel>
  );
}
